import { Controller } from "@hotwired/stimulus"
import { showConfirmDialog } from "../helpers/confirm_dialog"
import { openInstacartLink, csrfToken } from "../helpers/instacart_link"

// Tracks which grocery-item checkboxes are checked across the page, and drives
// the two shopping paths on the list:
//   • Shop on Instacart → a cart for the checked items, or the whole list.
//   • Move to pantry → "I shopped it myself" — stash the items as pantry staples.
// Selection just narrows the scope; the button labels always state it
// honestly. Removing individual items lives on each row's own edit form now
// (a trash icon), not as a bulk action here. Each form is a real Rails form
// — this controller funnels the relevant ids into hidden inputs right
// before submit.
export default class extends Controller {
  static targets = ["checkbox", "shopSection", "pantryForm", "selectAll", "boughtLabel"]
  static outlets = ["offline-grocery"]
  // Resting label for the move-to-inventory button — "Add to Inventory"
  // normally, "Mark as ordered" in Instacart mode (set from the view).
  static values = {
    boughtLabel: { type: String, default: "Add to Inventory" },
    instacartUrl: String // POST endpoint that builds a link for the chosen items
  }

  connect() {
    this.update()
  }

  // When a Turbo broadcast replaces the items container (e.g. an item moves to
  // the pantry mid-shop), the checkbox targets change. Re-evaluate so the
  // counts, labels, and section visibility stay truthful.
  checkboxTargetConnected() {
    this.update()
  }

  checkboxTargetDisconnected() {
    this.update()
  }

  update() {
    const count = this.selectedIds.length
    const total = this.checkboxTargets.length

    if (this.hasShopSectionTarget) this.shopSectionTarget.classList.toggle("hidden", total === 0)
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = total > 0 && count === total
      this.selectAllTarget.indeterminate = count > 0 && count < total
    }
    this.#syncLabels()
  }

  // "Select all" checkbox on the list — check/uncheck every item.
  toggleSelectAll(event) {
    this.setAllChecked(event.target.checked)
  }

  setAllChecked(checked) {
    this.checkboxTargets.forEach((cb) => { cb.checked = checked })
    this.update()
  }

  // Path 1 — selection-aware "Shop on Instacart". POST the checked ids — or none,
  // meaning the whole list — to build a link for just those items (the server
  // also stocks them into inventory); openInstacartLink owns the tab-opening,
  // double-tap guard, and fallback-on-failure behavior shared with
  // recipe-instacart#shop.
  async shopInstacart(event) {
    event.preventDefault()
    await openInstacartLink(event.currentTarget, async () => {
      const res = await fetch(this.instacartUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ grocery_item_ids: this.selectedIds })
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const { url } = await res.json()
      return url
    })
  }

  // Path 2 — "I shopped it myself": move the items to the pantry.
  async markBought() {
    if (!this.hasPantryFormTarget) return
    let ids = this.selectedIds
    if (ids.length === 0) {
      // Whole-list move is a bigger gesture than a checked subset — confirm it.
      ids = this.checkboxTargets.map((cb) => cb.value)
      if (ids.length === 0) return
      const confirmed = await showConfirmDialog(`Move all ${ids.length} items to your pantry?`)
      if (!confirmed) return
    }
    this.#moveToPantry(ids)
  }

  injectIds(form, ids) {
    form.querySelectorAll("input[name='grocery_item_ids[]'][data-bulk]").forEach((el) => el.remove())
    ids.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "grocery_item_ids[]"
      input.value = id
      input.dataset.bulk = "true"
      form.appendChild(input)
    })
  }

  get selectedIds() {
    return this.checkboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
  }

  #moveToPantry(ids) {
    if (!navigator.onLine && this.hasOfflineGroceryOutlet) {
      this.offlineGroceryOutlet.moveLater(ids)
      return
    }
    this.injectIds(this.pantryFormTarget, ids)
    this.pantryFormTarget.requestSubmit()
  }

  #syncLabels() {
    const count = this.selectedIds.length
    if (this.hasBoughtLabelTarget) {
      this.boughtLabelTarget.textContent = count > 0 ? `Add ${count} item${count === 1 ? "" : "s"} to Inventory` : this.boughtLabelValue
    }
  }
}
